import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import type { ICON } from '@george-labs.com/george-assets/icons';
import { Button } from '@george-labs.com/design-system';
import type {
    BUTTON_HIDDEN_LABEL,
    BUTTON_VARIANT,
} from '@george-labs.com/design-system/types/components/Button/NewButton';

import { useFormContext } from '@luna/form2';

import { A11YErrorInfo } from './A11YErrorInfo';

const ERROR_INFO_ID = 'a11y-error-info';

interface Props {
    children: ReactNode;
    'data-cy'?: string;
    icon?: ICON;
    variant?: BUTTON_VARIANT;
    className?: string;
    title?: string;
    disabled?: boolean;
    hiddenLabel?: BUTTON_HIDDEN_LABEL;
    formId?: string;
    isLoading?: boolean;
    onClick?(): void;
}

interface Statics {
    VARIANT: typeof BUTTON_VARIANT;
    ICON: typeof ICON;
    HIDDEN_LABEL: typeof BUTTON_HIDDEN_LABEL;
}

export const FormSubmitButton: FC<Props> & Statics = ({
    'data-cy': dataCy,
    icon,
    variant,
    className,
    onClick,
    title,
    children,
    disabled,
    hiddenLabel,
    formId,
    isLoading,
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const { isSubmitting } = useFormContext();

    return (
        <>
            <Button
                isLoading={isLoading ?? isSubmitting}
                disabled={disabled || isSubmitting}
                data-cy={dataCy}
                type="submit"
                variant={variant}
                icon={icon}
                title={title}
                className={className}
                onClick={onClick}
                hiddenLabel={hiddenLabel}
                form={formId}
                aria-describedby={ERROR_INFO_ID}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
            >
                {children}
            </Button>
            <A11YErrorInfo id={ERROR_INFO_ID} isLive={isFocused} />
        </>
    );
};

FormSubmitButton.VARIANT = Button.VARIANT;
FormSubmitButton.ICON = Button.ICON;
FormSubmitButton.HIDDEN_LABEL = Button.HIDDEN_LABEL;
